import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid as MuiGrid,
  Divider,
  Chip,
  CircularProgress,
  Button,
  Stack,
  Paper,
  Avatar,
  List,
  ListItem,
  ListItemText,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  Snackbar,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  Public as PublicIcon,
  Today as TodayIcon,
  Person as PersonIcon,
  Hotel as HotelIcon,
  Notes as NotesIcon,
  EventNote as EventNoteIcon,
  Feedback as FeedbackIcon,
  WhatsApp as WhatsAppIcon
} from '@mui/icons-material';
import dashboardService, { ReservationDetail as ReservationDetailType } from '../../services/dashboardService';
import feedbackService from '../../services/feedbackService';
import emailTemplateService, { EmailTemplate } from '../../services/emailTemplateService';
import reservationService from '../../services/reservationService';
import whatsappTemplateService, { WhatsAppTemplate } from '../../services/whatsappTemplateService';
import propertyService, { Property } from '../../services/propertyService';
import PropertyRecommendationDialog from './PropertyRecommendationDialog';
import { useSnackbar } from 'notistack';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const statusColors: Record<string, string> = {
  'Booked': 'primary',
  'Checked In': 'success',
  'Checked Out': 'info',
  'Cancelled': 'error',
  'No Show': 'warning',
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const formatDateTime = (dateString: string | null) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', { 
    style: 'currency', 
    currency: 'INR' 
  }).format(amount);
};

// Custom Grid component
const Grid = MuiGrid;

const ReservationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [reservation, setReservation] = useState<ReservationDetailType | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  
  // Add state for feedback features
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | ''>('');
  const [sendingFeedback, setSendingFeedback] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [templateError, setTemplateError] = useState(false);

  // Add state for WhatsApp features
  const [whatsappDialogOpen, setWhatsappDialogOpen] = useState(false);
  const [whatsappTemplates, setWhatsappTemplates] = useState<WhatsAppTemplate[]>([]);
  const [selectedWhatsappTemplate, setSelectedWhatsappTemplate] = useState<number | null>(null);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendSuccess, setSendSuccess] = useState<string | null>(null);

  const [recommendationDialogOpen, setRecommendationDialogOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [currentProperty, setCurrentProperty] = useState<Property | null>(null);

  const fetchReservation = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const data = await dashboardService.getReservationDetail(parseInt(id));
      setReservation(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching reservation:', err);
      setError('Failed to load reservation details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchReservation();
  }, [fetchReservation]);

  useEffect(() => {
    // Fetch active email templates
    const fetchEmailTemplates = async () => {
      try {
        const templates = await emailTemplateService.getAll(true);
        setEmailTemplates(templates);
        
        // If there's a template with "feedback" in the name, select it by default
        const feedbackTemplate = templates.find((template: EmailTemplate) => 
          template.name.toLowerCase().includes('feedback'));
        
        if (feedbackTemplate) {
          setSelectedTemplateId(feedbackTemplate.id as number);
        } else if (templates.length > 0) {
          setSelectedTemplateId(templates[0].id as number);
        }
      } catch (err) {
        console.error('Failed to fetch email templates:', err);
      }
    };
    
    fetchEmailTemplates();
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleBack = () => {
    navigate(-1);
  };

  // Add feedback-related functions
  const handleOpenFeedbackDialog = () => {
    if (!reservation?.email) {
      setSnackbarSeverity('error');
      setSnackbarMessage('This reservation does not have an email address for the guest');
      setSnackbarOpen(true);
      return;
    }
    
    setFeedbackDialogOpen(true);
  };

  const handleCloseFeedbackDialog = () => {
    setFeedbackDialogOpen(false);
    setTemplateError(false);
  };

  const handleSendFeedbackEmail = async () => {
    if (!selectedTemplateId) {
      setTemplateError(true);
      return;
    }
    
    if (!id) return;
    
    try {
      setSendingFeedback(true);
      
      await feedbackService.sendFeedbackEmail({
        reservation_id: parseInt(id),
        email_template_id: selectedTemplateId as number
      });
      
      handleCloseFeedbackDialog();
      setSnackbarSeverity('success');
      setSnackbarMessage('Feedback request email sent successfully');
      setSnackbarOpen(true);
    } catch (err: any) {
      console.error('Failed to send feedback email:', err);
      setSnackbarSeverity('error');
      setSnackbarMessage(err.response?.data?.message || 'Failed to send feedback email');
      setSnackbarOpen(true);
    } finally {
      setSendingFeedback(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const fetchTemplates = async () => {
    try {
      const data = await whatsappTemplateService.getAll();
      setWhatsappTemplates(data);
    } catch (err) {
      console.error('Failed to load WhatsApp templates:', err);
    }
  };

  const handleSendWhatsApp = async () => {
    if (!selectedWhatsappTemplate) return;

    try {
      setSendingMessage(true);
      setSendError(null);
      setSendSuccess(null);

      const result = await reservationService.sendWhatsAppMessage(
        Number(id),
        selectedWhatsappTemplate
      );

      if (result.success) {
        setSendSuccess('WhatsApp message sent successfully');
        setWhatsappDialogOpen(false);
      } else {
        setSendError(result.message || 'Failed to send WhatsApp message');
      }
    } catch (err) {
      setSendError('Failed to send WhatsApp message');
      console.error(err);
    } finally {
      setSendingMessage(false);
    }
  };

  const fetchPropertyDetails = async (propertyId: string) => {
    try {
      const properties = await propertyService.getProperties();
      const property = properties.find(p => p.property_id === propertyId);
      if (property) {
        setCurrentProperty(property);
      }
    } catch (err) {
      console.error('Error fetching property details:', err);
      enqueueSnackbar('Failed to load property details', { variant: 'error' });
    }
  };

  useEffect(() => {
    if (reservation?.property && recommendationDialogOpen) {
      fetchPropertyDetails(reservation.property);
    }
  }, [reservation?.property, recommendationDialogOpen]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 3 }}>
          Back
        </Button>
        <Paper sx={{ p: 3, bgcolor: 'error.light', borderRadius: 2 }}>
          <Typography color="error.dark">{error}</Typography>
          <Button
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
            onClick={() => window.location.reload()}
          >
            Retry
          </Button>
        </Paper>
      </Box>
    );
  }

  if (!reservation) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 3 }}>
          Back
        </Button>
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography>Reservation not found</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 3 }}>
        Back to Reservations
      </Button>

      {/* Reservation Header */}
      <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid style={{ gridColumn: 'span 8' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', mr: 2 }}>
                  Reservation #{reservation.crs_no}
                </Typography>
                <Chip
                  label={reservation.reservation_status}
                  color={statusColors[reservation.reservation_status] as any || 'default'}
                  size="small"
                />
              </Box>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                {reservation.guest_name}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} divider={<Divider orientation="vertical" flexItem />}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <TodayIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="body2">
                      Check-in: {reservation.timeline && reservation.timeline.find(e => e.event === 'Checked In') && (
                      formatDateTime(reservation.timeline.find(e => e.event === 'Checked In')?.date || null)
                  )}
                    </Typography>
                  </Box>
                  
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <TodayIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="body2">
                      Check-out: {reservation.timeline && reservation.timeline.find(e => e.event === 'Checked Out') && (
                      formatDateTime(reservation.timeline.find(e => e.event === 'Checked Out')?.date || null)
                  )}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <HotelIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="body2">
                    {reservation.property_name || 'N/A'}
                  </Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid style={{ gridColumn: 'span 4', display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
              <Stack direction="row" spacing={1}>
                {/* <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<FeedbackIcon />}
                  onClick={handleOpenFeedbackDialog}
                >
                  Request Feedback
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<WhatsAppIcon />}
                  onClick={() => setWhatsappDialogOpen(true)}
                >
                  Send WhatsApp Message
                </Button> */}
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Feedback Email Dialog */}
      <Dialog
        open={feedbackDialogOpen}
        onClose={handleCloseFeedbackDialog}
        aria-labelledby="feedback-dialog-title"
      >
        <DialogTitle id="feedback-dialog-title">Send Feedback Request</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Send a feedback request email to {reservation.guest_name} ({reservation.email})
          </DialogContentText>
          <FormControl 
            fullWidth 
            margin="normal" 
            required 
            error={templateError}
          >
            <InputLabel id="template-select-label">Email Template</InputLabel>
            <Select
              labelId="template-select-label"
              value={selectedTemplateId}
              label="Email Template"
              onChange={(e) => {
                setSelectedTemplateId(e.target.value as number);
                setTemplateError(false);
              }}
            >
              {emailTemplates.map((template) => (
                <MenuItem key={template.id} value={template.id}>
                  {template.name}
                </MenuItem>
              ))}
            </Select>
            {templateError && (
              <FormHelperText>Please select an email template</FormHelperText>
            )}
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFeedbackDialog} disabled={sendingFeedback}>
            Cancel
          </Button>
          <Button 
            onClick={handleSendFeedbackEmail} 
            variant="contained" 
            color="primary"
            disabled={sendingFeedback}
          >
            {sendingFeedback ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* WhatsApp Message Dialog */}
      <Dialog
        open={whatsappDialogOpen}
        onClose={() => setWhatsappDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send WhatsApp Message</DialogTitle>
        <DialogContent>
          {sendError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {sendError}
            </Alert>
          )}
          {sendSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {sendSuccess}
            </Alert>
          )}
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Select Template</InputLabel>
            <Select
              value={selectedWhatsappTemplate || ''}
              onChange={(e) => setSelectedWhatsappTemplate(e.target.value as number)}
              label="Select Template"
            >
              {whatsappTemplates.map((template) => (
                <MenuItem key={template.id} value={template.id}>
                  {template.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWhatsappDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSendWhatsApp}
            variant="contained"
            disabled={sendingMessage || !selectedWhatsappTemplate}
            startIcon={sendingMessage ? <CircularProgress size={20} /> : null}
          >
            Send Message
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbarSeverity} 
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Tabs for different sections */}
      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="reservation details tabs">
            <Tab label="Guest Details" />
            <Tab label="Room Details" />
            <Tab label="Notes" />
            <Tab label="Timeline" />
          </Tabs>
        </Box>

        {/* Guest Details Tab */}
        <TabPanel value={tabValue} index={0}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Guest Information</Typography>
              <Grid container spacing={3}>
                <Grid style={{ gridColumn: 'span 6' }}>
                  <List>
                    <ListItem>
                      <Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>
                        <PersonIcon />
                      </Avatar>
                      <ListItemText
                        primary="Guest Name"
                        secondary={reservation.guest_name}
                      />
                    </ListItem>
                    <ListItem>
                      <Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>
                        <EmailIcon />
                      </Avatar>
                      <ListItemText
                        primary="Email"
                        secondary={reservation.email || 'Not provided'}
                      />
                    </ListItem>
                    <ListItem>
                      <Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>
                        <PhoneIcon />
                      </Avatar>
                      <ListItemText
                        primary="Phone"
                        secondary={reservation.phone || 'Not provided'}
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid style={{ gridColumn: 'span 6' }}>
                  <List>
                    <ListItem>
                      <Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>
                        <HomeIcon />
                      </Avatar>
                      <ListItemText
                        primary="Address"
                        secondary={reservation.address || 'Not provided'}
                      />
                    </ListItem>
                    <ListItem>
                      <Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>
                        <PublicIcon />
                      </Avatar>
                      <ListItemText
                        primary="Country"
                        secondary={reservation.country || 'Not provided'}
                      />
                    </ListItem>
                    <ListItem>
                      <Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>
                        <PersonIcon />
                      </Avatar>
                      <ListItemText
                        primary="Guests"
                        secondary={`${reservation.adults} Adults, ${reservation.children} Children`}
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>

              {reservation.special_requests && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" gutterBottom>Special Requests</Typography>
                  <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Typography variant="body2">
                      {reservation.special_requests}
                    </Typography>
                  </Paper>
                </>
              )}
            </CardContent>
          </Card>
        </TabPanel>

        {/* Room Details Tab */}
        <TabPanel value={tabValue} index={1}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Room Information</Typography>
              
              <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                <Table sx={{ minWidth: 650 }} aria-label="room details table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Room</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Rate</TableCell>
                      <TableCell align="right">Guests</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reservation.room_details.map((room, index) => (
                      <TableRow key={index}>
                        <TableCell component="th" scope="row">
                          {room.name}
                        </TableCell>
                        <TableCell>{room.type}</TableCell>
                        <TableCell align="right">{formatCurrency(room.rate)}</TableCell>
                        <TableCell align="right">{room.guests}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </TabPanel>

        {/* Notes Tab */}
        <TabPanel value={tabValue} index={2}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <NotesIcon sx={{ mr: 1 }} /> Notes & Comments
              </Typography>
              
              {reservation.notes.length > 0 ? (
                <List>
                  {reservation.notes.map((note, index) => (
                    <ListItem key={index} alignItems="flex-start" sx={{ bgcolor: 'background.default', mb: 2, borderRadius: 2 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle2">{note.added_by}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(note.date).toLocaleString()}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Typography
                            sx={{ display: 'inline' }}
                            component="span"
                            variant="body2"
                            color="text.primary"
                          >
                            {note.text}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No notes available for this reservation
                </Typography>
              )}
            </CardContent>
          </Card>
        </TabPanel>

        {/* Timeline Tab */}
        <TabPanel value={tabValue} index={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <EventNoteIcon sx={{ mr: 1 }} /> Reservation Timeline
              </Typography>
              
              {reservation.timeline.length > 0 ? (
                <Box sx={{ position: 'relative', pl: 3, '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: '9px',
                  top: 0,
                  bottom: 0,
                  width: '2px',
                  bgcolor: 'divider',
                  borderRadius: '4px'
                }}}>
                  {reservation.timeline.map((event, index) => (
                    <Box key={index} sx={{ mb: 3, position: 'relative' }}>
                      <Box sx={{ 
                        width: '20px', 
                        height: '20px', 
                        borderRadius: '50%', 
                        bgcolor: 'primary.main',
                        position: 'absolute',
                        left: '-25px',
                        top: '2px',
                        zIndex: 1
                      }} />
                      <Box>
                        <Typography variant="subtitle2">{event.event}</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(event.date).toLocaleString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            by {event.user}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No timeline events available for this reservation
                </Typography>
              )}
            </CardContent>
          </Card>
        </TabPanel>
      </Box>

      <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setRecommendationDialogOpen(true)}
        >
          Recommend Alternative Property
        </Button>
        
        {/* ... other buttons ... */}
      </Box>

      {reservation && (
        <PropertyRecommendationDialog
          open={recommendationDialogOpen}
          onClose={() => setRecommendationDialogOpen(false)}
          reservationId={reservation?.id || 0}
          currentProperty={currentProperty || {
            id: 0,
            name: reservation?.property_name || '',
            property_id: reservation?.property || '',
            address: '',
            city: '',
            state: '',
            country: ''
          }}
          onSuccess={() => {
            setRecommendationDialogOpen(false);
            fetchReservation();
          }}
        />
      )}
    </Box>
  );
};

export default ReservationDetail; 